import { useToast } from "@/shadcnComponents/ui/use-toast";
import axios from "axios";
import {
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

interface GeneralContextProps {
  Loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  DashboardLoading: boolean;
  setDashboardLoading: React.Dispatch<React.SetStateAction<boolean>>;
  HeartLoading: boolean;
  setHeartLoading: React.Dispatch<React.SetStateAction<boolean>>;
  SidebarChipsCount: {
    profile: number;
    collab: number;
    monetization: number;
    content: number;
    audience: number;
    charges: number;
    referral: number;
  };
  setSidebarChipsCount: React.Dispatch<
    SetStateAction<{
      profile: number;
      collab: number;
      monetization: number;
      content: number;
      audience: number;
      charges: number;
      referral: number;
    }>
  >;
  useWindowSize: () => { width: number; height: number };
  exchangeRates: {
    USD: number;
    EUR: number;
    INR: number;
  };
  handleDownloadPdf: (string) => void;
  handleDownloadImage: (string) => void;
  showErrorToast: (title: string, message?: string) => void;
}

interface GeneralProviderProps {
  children: React.ReactNode;
}

const GeneralContext = createContext<GeneralContextProps | null>(null);

export const useGeneralSettings = () => {
  return useContext(GeneralContext);
};

const CACHE_KEY = "exchangeRateCache";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const GeneralProvider: React.FC<GeneralProviderProps> = (props) => {
  const { toast } = useToast();
  const [Loading, setLoading] = useState(false);
  const [DashboardLoading, setDashboardLoading] = useState(false);
  const [HeartLoading, setHeartLoading] = useState(false);

  const [SidebarChipsCount, setSidebarChipsCount] = useState({
    profile: 0,
    collab: 0,
    monetization: 0,
    content: 0,
    audience: 0,
    charges: 0,
    referral: 0,
  });

  const isCacheValid = (cachedData) => {
    if (!cachedData) return false;
    const now = new Date().getTime();
    return now - cachedData.timestamp < CACHE_DURATION;
  };

  const [exchangeRates, setExchangeRates] = useState({
    USD: 0,
    EUR: 0, // 1 USD = 0.92 EUR
    INR: 0, // 1 USD = 83.12 INR
  });

  const handleDownloadPdf = async (pdfUrl) => {
    try {
      const encodedUrl = encodeURI(pdfUrl);
      // Fetch the image
      const response = await fetch(
        `${
          import.meta.env.VITE_BACKEND_SERVER_URL
        }/api/file/proxyImage?imageUrl=${encodedUrl}`,
        {
          method: "GET",
          headers: {
            Origin: window.location.origin,
            "Access-Control-Request-Method": "GET",
          },
        }
      );
      const blob = await response.blob();

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element
      const link = document.createElement("a");
      link.href = blobUrl;

      // Extract filename from S3 URL
      const filename = pdfUrl.split("/").pop() || "file.pdf";
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  const handleDownloadImage = async (imageUrl) => {
    try {
      const encodedUrl = encodeURI(imageUrl);
      // Fetch the image
      const response = await fetch(
        `${
          import.meta.env.VITE_BACKEND_SERVER_URL
        }/api/file/proxyImage?imageUrl=${encodedUrl}`,
        {
          method: "GET",
          headers: {
            Origin: window.location.origin,
            "Access-Control-Request-Method": "GET",
          },
        }
      );
      const blob = await response.blob();

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element
      const link = document.createElement("a");
      link.href = blobUrl;

      // Extract filename from S3 URL
      const filename = imageUrl.split("/").pop() || "image.jpg";
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    useEffect(() => {
      const handleResize = () => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      };

      window.addEventListener("resize", handleResize);

      // Cleanup event listener on component unmount
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, []);

    return windowSize;
  };

  // Function to fetch fresh exchange rates
  const fetchExchangeRates = async () => {
    try {
      const result = await axios.get(
        `https://api.frankfurter.dev/v1/latest?base=USD`
      );

      if (result?.data?.base === "USD") {
        const { rates } = result.data;
        const ratesData = {
          rates: {
            USD: result.data?.amount,
            EUR: rates?.EUR,
            INR: rates?.INR,
          },
          timestamp: new Date().getTime(),
        };

        // Save to localStorage
        localStorage.setItem(CACHE_KEY, JSON.stringify(ratesData));
        setExchangeRates(ratesData.rates);
      }
    } catch (error) {
      console.error("Error fetching exchange rates:", error);
    }
  };

  //function to show toast error and success
  const showErrorToast = (title: string, message?: string) => {
    toast({
      variant: "destructive",
      title: title,
      description: message,
    });
  };

  useEffect(() => {
    const initializeData = async () => {
      // Get cached exchange rates
      const cachedData = JSON.parse(localStorage.getItem(CACHE_KEY));

      if (isCacheValid(cachedData)) {
        // Use cached data if it's still valid
        setExchangeRates(cachedData.rates);
      } else {
        // Fetch fresh data if cache is invalid or missing
        await fetchExchangeRates();
      }
    };

    initializeData();

    // Optional: Set up periodic refresh
    const refreshInterval = setInterval(() => {
      fetchExchangeRates();
    }, CACHE_DURATION);

    // Cleanup interval on component unmount
    return () => clearInterval(refreshInterval);
  }, []);

  return (
    <GeneralContext.Provider
      value={{
        Loading,
        setLoading,
        SidebarChipsCount,
        setSidebarChipsCount,
        DashboardLoading,
        setDashboardLoading,
        HeartLoading,
        setHeartLoading,
        useWindowSize,
        exchangeRates,
        handleDownloadPdf,
        handleDownloadImage,
        showErrorToast,
      }}
    >
      {props.children}
    </GeneralContext.Provider>
  );
};
